/**
 * graft/A7 (Rapid QA): the AI-overview stream for the reserved answer slot.
 *
 * Talks to BTNet-Search `POST /api/v2/answer` (graft/rapid-qa), an SSE
 * stream that lands next to the committed result list:
 *
 *   answer_start       -> phase "connecting" (mode, wantsLlm, budgets)
 *   answer_facts       -> phase "facts"      retrieval landed (result_count)
 *   answer_delta*      -> phase "streaming"  prose tokens appended in place
 *   answer_sources     -> the records the prose cited ([Title](rec:rN))
 *   answer_done        -> phase "done"
 *   answer_unavailable -> phase "unavailable" (reason drives the outcome)
 *
 * v2 (2026-09-22) — the overview is SELECTIVE and it is not a result list:
 *
 *  1. WHICH queries. Only a query that needs an answer or a summary gets an
 *     overview: a question, a quantity ask, a status set ("unpaid bills"),
 *     a superlative ("latest invoice from Lezzer") or a dated kind ask
 *     ("bills this week"). `wantsOverview` mirrors the server's
 *     rapid_qa/shape.py v2 rule for rule; a lookup never even calls the
 *     endpoint. There is no facts strip and no "Summarize" affordance any
 *     more — a name search gets its result list and nothing else.
 *
 *  2. WHEN it runs. Never per keystroke. `useRapidOverviewForSearch` starts
 *     the stream only at a stopping point: the live input has been still
 *     for RAPID_SETTLE_MS, or the user committed explicitly — Enter, a
 *     "Try asking" chip, a did-you-mean, a URL on the page — via
 *     `commit(query)`. Both wait for the search for that exact query to
 *     exist (the overview rides the search's uuid) and start at most once
 *     per searchId. Typing over a running overview aborts it.
 *
 *  3. The slot's HEIGHT is still reserved from the query TEXT, not from the
 *     network (UX rule D4: nothing here may move the result list): the
 *     driver exposes `armed` — the live input is overview-shaped — and the
 *     slot reserves the card frame the moment that is true, then only ever
 *     changes CONTENT inside it. An overview that turns out to have nothing
 *     to say collapses the frame once, at stream end, never mid-stream.
 *
 * Lifecycle mirrors useAiSearch: one in-flight request, aborted and
 * sequence-guarded on every new query, so a stale token can never paint over
 * a newer answer. A 404 (route absent, or the server's RAPID_QA_ENABLED off)
 * marks the endpoint dead for the session and the hook goes inert, the same
 * way useSearchSuggest does.
 *
 * Gating: isAiSearchRapidQaEnabled() (client kill switch, default ON) AND the
 * server's RAPID_QA_ENABLED (master switch: off => 404 => inert).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { isAiSearchRapidQaEnabled } from "../aiSearchFlags";
import { devContextParams, loadDevContext } from "../devContext";

export type RapidAnswerPhase =
    | "idle"
    | "connecting"
    | "facts"
    | "streaming"
    | "done"
    | "unavailable"
    | "error";

export type RapidAnswerMode = "auto" | "llm" | "facts";

export interface IRapidAnswerFacts {
    top: {
        handle: string;
        entity: string;
        title: string;
        job: string | null;
        match_line: string | null;
        resolver_matched: boolean;
    } | null;
    result_count: number;
    kinds: { kind: string; label: string; count: number }[];
}

export interface IRapidAnswerSource {
    handle: string;
    entity: string;
    id: string;
    title: string;
    job: string | null;
    /** Owning jobsite id when the record carries one — job-scoped entity links need it. */
    job_id?: string | null;
}

export interface IRapidAnswerState {
    phase: RapidAnswerPhase;
    /** The committed query this answer belongs to (not the live input). */
    query: string;
    /** Correlation id of the search this answer rides with. */
    searchId: string;
    /** The mode this stream was requested with (auto in the UI; llm/facts are API/bench levers). */
    requestedMode: RapidAnswerMode;
    /** Server's shape verdict: question | aggregate | set | superlative | temporal | lookup. */
    mode: string | null;
    /**
     * Whether prose is expected. Seeded true at start() (the UI only starts
     * overview-shaped queries) and confirmed by answer_start; the server's
     * verdict wins for mode=auto.
     */
    wantsLlm: boolean;
    facts: IRapidAnswerFacts | null;
    text: string;
    sources: IRapidAnswerSource[];
    /** answer_unavailable.reason, or the HTTP/network failure class. */
    reason: string | null;
    factsMs: number | null;
    ttftMs: number | null;
    totalMs: number | null;
    softBudgetMs: number;
    hardBudgetMs: number;
    grounded: boolean | null;
    truncated: boolean;
    cacheHit: boolean;
    model: string | null;
}

export const RAPID_ANSWER_IDLE: IRapidAnswerState = {
    phase: "idle",
    query: "",
    searchId: "",
    requestedMode: "auto",
    mode: null,
    wantsLlm: false,
    facts: null,
    text: "",
    sources: [],
    reason: null,
    factsMs: null,
    ttftMs: null,
    totalMs: null,
    softBudgetMs: 2500,
    hardBudgetMs: 4000,
    grounded: null,
    truncated: false,
    cacheHit: false,
    model: null,
};

// ── Shape rules — client mirror of app/services/rapid_qa/shape.py (v2) ──────

export type OverviewShape =
    | "question"
    | "aggregate"
    | "set"
    | "superlative"
    | "temporal"
    | "lookup"
    | "none";

const WORD_RE = /[A-Za-z0-9$#'&/.-]+/g;
const WH_OPEN_RE =
    /^(who|whom|whose|what|what's|whats|when|when's|where|where's|why|how|how's|which)\b/i;
const AUX_OPEN_RE = /^(is|are|was|were|do|does|did|can|could|will|would|should|has|have|had|am)\b/i;
const ASK_OPEN_RE =
    /^(show me|tell me|give me|list (?:all|the|every|our|my)|summari[sz]e|compare|break ?down|explain|describe|overview of|recap)\b/i;
const AGGREGATE_RE =
    /\b(how many|how much|totals?|totall?ed|sum of|count of|number of|average|avg|median|per (?:job|vendor|sub|month|week|day|cost code|client)|by (?:vendor|job|sub|month|week|cost code|client|status|category)|break ?down|breakdown|overall|altogether|in total)\b/i;
const STATUS_RE =
    /\b(unpaid|outstanding|overdue|past[- ]due|open|pending|approved|unapproved|unread|unscheduled|unsigned|declined|rejected|late|active|inactive|completed?|closed|paid|unbilled|uninvoiced|released|unreleased|draft|in progress|behind schedule|on hold|expired|expiring|due)\b/i;
const KIND_RE =
    /\b(bills?|invoices?|owner invoices?|payments?|receipts?|credit memos?|purchase orders?|pos?|change orders?|estimates?|bids?|proposals?|allowances?|selections?|schedule(?: items?)?|tasks?|to-?dos?|daily logs?|logs?|documents?|docs?|files?|photos?|videos?|rfis?|warrant(?:y|ies)|claims?|messages?|comments?|subs?|subcontractors?|vendors?|contacts?|clients?|customers?|leads?|jobs?|jobsites?|projects?|cost codes?|line items?|lien waivers?|time ?cards?|time clock|surveys?|specifications?|specs?|users?|people|staff|employees?|contracts?)\b/i;
// "last / first / next" rank a record ("last bill from Lezzer") unless they
// open a window ("last month", "next week", "last 30 days") — that is temporal.
const RANK_RE =
    /\b(?:latest|most recent|newest|oldest|biggest|largest|smallest|highest|lowest|earliest|top(?: \d+)?|upcoming|recent|(?:last|first|next)(?! (?:week|month|quarter|year|few|couple|two|three|several|\d)))\b/i;
const LAST_TIME_RE = /\b(last|first) time\b/i;
// The common date windows people type; the server's temporal parser is the
// richer authority, this only decides whether the client asks at all.
const WINDOW_RE =
    /\b(today|yesterday|tomorrow|this (?:week|month|quarter|year)|last (?:week|month|quarter|year|\d+ (?:days?|weeks?|months?|years?))|next (?:week|month|quarter|year|\d+ (?:days?|weeks?|months?))|past (?:week|month|quarter|year|\d+ (?:days?|weeks?|months?|years?))|(?:in|since|before|after|from|during) (?:january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sept?|oct|nov|dec)\b|q[1-4]\b|\d+ days? ago|between \d+ and \d+ days)/i;

function isQuestionShaped(q: string, n: number): boolean {
    if (q.endsWith("?")) return true;
    if (n >= 2 && WH_OPEN_RE.test(q)) return true;
    if (n >= 3 && AUX_OPEN_RE.test(q)) return true;
    if (n >= 2 && ASK_OPEN_RE.test(q)) return true;
    return false;
}

/** Same verdict, same priority order, as the server's classify(). */
export function classifyOverview(query: string): OverviewShape {
    const q = query.trim();
    const n = (q.match(WORD_RE) ?? []).length;
    if (n === 0) return "none";
    const hasKind = KIND_RE.test(q);
    if (isQuestionShaped(q, n)) return "question";
    if (n >= 2 && AGGREGATE_RE.test(q)) return "aggregate";
    if (hasKind && STATUS_RE.test(q)) return "set";
    if ((hasKind && RANK_RE.test(q)) || LAST_TIME_RE.test(q)) return "superlative";
    if (hasKind && WINDOW_RE.test(q)) return "temporal";
    return "lookup";
}

/** True when the query earns an AI overview (anything but a lookup). */
export function wantsOverview(query: string): boolean {
    const shape = classifyOverview(query);
    return shape !== "lookup" && shape !== "none";
}

/** Parsed SSE frame. Exported for the replay tests. */
export interface IRapidAnswerEvent {
    event: string;
    data: Record<string, unknown>;
}

/**
 * Incremental SSE parser shared with the tests: feeds raw text chunks and
 * returns complete frames. Frames end at a blank line; `data:` lines
 * concatenate. Tolerates CRLF and a missing trailing newline at stream end.
 */
export function createSseParser() {
    let buffer = "";
    let currentEvent = "";
    let dataLines: string[] = [];
    const flush = (out: IRapidAnswerEvent[]) => {
        if (currentEvent && dataLines.length > 0) {
            try {
                out.push({
                    event: currentEvent,
                    data: JSON.parse(dataLines.join("")) as Record<string, unknown>,
                });
            } catch {
                // malformed frame: drop it, keep the stream alive
            }
        }
        currentEvent = "";
        dataLines = [];
    };
    return {
        feed(chunk: string, final = false): IRapidAnswerEvent[] {
            buffer += chunk.replace(/\r\n/g, "\n");
            const out: IRapidAnswerEvent[] = [];
            let idx = buffer.indexOf("\n");
            while (idx !== -1) {
                const line = buffer.slice(0, idx);
                buffer = buffer.slice(idx + 1);
                if (line.startsWith("event:")) {
                    currentEvent = line.slice(6).trim();
                } else if (line.startsWith("data:")) {
                    dataLines.push(line.slice(5).trim());
                } else if (line.trim() === "") {
                    flush(out);
                }
                idx = buffer.indexOf("\n");
            }
            if (final) {
                if (buffer.startsWith("data:")) dataLines.push(buffer.slice(5).trim());
                buffer = "";
                flush(out);
            }
            return out;
        },
    };
}

/** Pure reducer: applies one server event to the state. Exported for tests. */
export function applyRapidAnswerEvent(
    prev: IRapidAnswerState,
    ev: IRapidAnswerEvent,
    elapsedMs: number,
): IRapidAnswerState {
    const d = ev.data;
    switch (ev.event) {
        case "answer_start":
            return {
                ...prev,
                phase: "connecting",
                mode: typeof d.mode === "string" ? d.mode : null,
                // The server's verdict wins for mode=auto; a forced llm
                // request (API/bench) keeps the card.
                wantsLlm: prev.requestedMode === "llm" ? true : d.wants_llm === true,
                softBudgetMs:
                    typeof d.soft_budget_ms === "number" ? d.soft_budget_ms : prev.softBudgetMs,
                hardBudgetMs:
                    typeof d.hard_budget_ms === "number" ? d.hard_budget_ms : prev.hardBudgetMs,
                cacheHit: d.cache_hit === true,
                model: typeof d.model === "string" ? d.model : null,
            };
        case "answer_facts":
            return {
                ...prev,
                phase: prev.phase === "streaming" ? prev.phase : "facts",
                facts: {
                    top: (d.top as IRapidAnswerFacts["top"]) ?? null,
                    result_count: typeof d.result_count === "number" ? d.result_count : 0,
                    kinds: Array.isArray(d.kinds) ? (d.kinds as IRapidAnswerFacts["kinds"]) : [],
                },
                factsMs: prev.factsMs ?? elapsedMs,
            };
        case "answer_delta":
            return {
                ...prev,
                phase: "streaming",
                wantsLlm: true,
                text: prev.text + (typeof d.text === "string" ? d.text : ""),
                ttftMs: prev.ttftMs ?? elapsedMs,
            };
        case "answer_sources":
            return {
                ...prev,
                sources: Array.isArray(d.sources) ? (d.sources as IRapidAnswerSource[]) : [],
            };
        case "answer_done":
            return {
                ...prev,
                phase: "done",
                wantsLlm: true,
                // The server's final text is authoritative (hold-back flush,
                // redaction, polish); it supersedes the concatenated deltas.
                text: typeof d.text === "string" && d.text.length > 0 ? d.text : prev.text,
                totalMs: elapsedMs,
                ttftMs: prev.ttftMs ?? (typeof d.ttft_ms === "number" ? d.ttft_ms : null),
                grounded: typeof d.grounded === "boolean" ? d.grounded : null,
                truncated: d.truncated === true,
                cacheHit: d.cache_hit === true || prev.cacheHit,
                model: typeof d.model === "string" ? d.model : prev.model,
            };
        case "answer_unavailable":
            return {
                ...prev,
                phase: "unavailable",
                reason: typeof d.reason === "string" ? d.reason : "unavailable",
                totalMs: elapsedMs,
            };
        default:
            return prev;
    }
}

// ── The server flag probe ───────────────────────────────────────────────────

/**
 * `GET /v2/answer/status` once per base url per page load. The server's
 * RAPID_QA_ENABLED is the deployment-level switch for the whole surface
 * (2026-09-22, Alan: "turn off the AI overview for now on both ephemerals
 * with a flag" — both call the dev search service): until the probe says
 * enabled, nothing Rapid-QA shows — no frame, no "Try asking" chips — and a
 * disabled server costs one small request instead of a frame that appears
 * and then vanishes on a 404. Turning the overview back on is a server flag
 * flip; no client rebuild.
 *
 * The probe is lazy: the driver fires it the first time the live input is
 * overview-shaped (or on commit), and the surfaces fire it when they open,
 * so an idle page load never pays for a token or a request.
 */
const probeCache = new Map<string, Promise<boolean>>();

export function probeRapidQa(
    searchBaseUrl: string,
    getToken?: () => Promise<string>,
): Promise<boolean> {
    const cached = probeCache.get(searchBaseUrl);
    if (cached) return cached;
    const p = (async () => {
        try {
            const token = getToken ? await getToken().catch(() => "") : "";
            const res = await fetch(`${searchBaseUrl}v2/answer/status`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) return false;
            const body = (await res.json()) as { enabled?: unknown };
            return body.enabled === true;
        } catch {
            // A network failure is not a verdict: let the next mount ask again.
            probeCache.delete(searchBaseUrl);
            return false;
        }
    })();
    probeCache.set(searchBaseUrl, p);
    return p;
}

/** Tests: forget probe verdicts between cases. */
export function resetRapidQaProbe(): void {
    probeCache.clear();
}

export interface IUseRapidAnswer {
    state: IRapidAnswerState;
    /** Start (or restart) the answer stream for a committed query. */
    start: (query: string, searchId: string, mode?: RapidAnswerMode) => void;
    /** Abort the in-flight stream and return to idle (new search / close / clear). */
    cancel: () => void;
    /**
     * True only once the server said RAPID_QA_ENABLED (probe) and nothing has
     * 404'd since. False before the probe, when the server flag is off, when
     * the route is absent, and when the client kill switch is off.
     */
    available: boolean;
    /** Ask the server whether Rapid QA is on (idempotent; cached per page load). */
    probe: () => void;
}

export function useRapidAnswer(
    searchBaseUrl: string,
    getToken?: () => Promise<string>,
): IUseRapidAnswer {
    // Read once per mount: a flag flip mid-session must not switch contracts.
    const [enabled] = useState(isAiSearchRapidQaEnabled);
    const [state, setState] = useState<IRapidAnswerState>(RAPID_ANSWER_IDLE);
    // False until the server says so — see probeRapidQa.
    const [available, setAvailable] = useState(false);
    const deadRef = useRef(false);
    const probeStartedRef = useRef(false);
    const mountedRef = useRef(false);
    const seqRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);
    const getTokenRef = useRef(getToken);
    useEffect(() => {
        getTokenRef.current = getToken;
    });

    const cancel = useCallback(() => {
        seqRef.current += 1;
        abortRef.current?.abort();
        abortRef.current = null;
        setState(RAPID_ANSWER_IDLE);
    }, []);

    const markDead = useCallback(() => {
        deadRef.current = true;
        setAvailable(false);
        setState(RAPID_ANSWER_IDLE);
    }, []);

    const probe = useCallback(() => {
        if (!enabled || !searchBaseUrl || deadRef.current || probeStartedRef.current) return;
        probeStartedRef.current = true;
        void probeRapidQa(searchBaseUrl, getTokenRef.current).then((ok) => {
            if (!mountedRef.current) return;
            if (ok) {
                setAvailable(true);
            } else {
                // Not a verdict for the page: a network failure may resolve
                // on the next open, so only the cached "server said no"
                // path marks this mount dead.
                probeStartedRef.current = false;
                if (probeCache.has(searchBaseUrl)) markDead();
            }
        });
    }, [enabled, searchBaseUrl, markDead]);

    const start = useCallback(
        (query: string, searchId: string, mode: RapidAnswerMode = "auto") => {
            const q = query.trim();
            if (!enabled || deadRef.current || !searchBaseUrl || q.length === 0) {
                return;
            }
            abortRef.current?.abort();
            const ctrl = new AbortController();
            abortRef.current = ctrl;
            const my = ++seqRef.current;
            const t0 = performance.now();

            void (async () => {
                try {
                    // Never paint a card the server will refuse: the probe
                    // (cached after the first answer) gates the request.
                    const ok = await probeRapidQa(searchBaseUrl, getTokenRef.current);
                    if (my !== seqRef.current) return;
                    if (!ok) {
                        if (probeCache.has(searchBaseUrl)) markDead();
                        return;
                    }
                    setAvailable(true);
                    setState({
                        ...RAPID_ANSWER_IDLE,
                        phase: "connecting",
                        query: q,
                        searchId,
                        requestedMode: mode,
                        wantsLlm: mode !== "facts",
                    });
                    const tokenFn = getTokenRef.current;
                    const token = tokenFn ? await tokenFn().catch(() => "") : "";
                    if (my !== seqRef.current) return;
                    const devParams = devContextParams(loadDevContext());
                    const res = await fetch(`${searchBaseUrl}v2/answer${devParams}`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify({ query: q, mode, stream: true, search_id: searchId }),
                        signal: ctrl.signal,
                    });
                    if (my !== seqRef.current) return;
                    if (res.status === 404) {
                        // Server has no Rapid QA (route absent or the flag
                        // flipped off since the probe).
                        markDead();
                        return;
                    }
                    if (!res.ok || !res.body) {
                        setState((prev) => ({
                            ...prev,
                            phase: "error",
                            reason: `http_${res.status}`,
                            totalMs: Math.round(performance.now() - t0),
                        }));
                        return;
                    }
                    const reader = res.body.getReader();
                    const decoder = new TextDecoder();
                    const parser = createSseParser();
                    const apply = (events: IRapidAnswerEvent[]) => {
                        if (events.length === 0 || my !== seqRef.current) return;
                        const elapsed = Math.round(performance.now() - t0);
                        setState((prev) =>
                            events.reduce(
                                (acc, ev) => applyRapidAnswerEvent(acc, ev, elapsed),
                                prev,
                            ),
                        );
                    };
                    for (;;) {
                        const { done, value } = await reader.read();
                        if (done) {
                            apply(parser.feed("", true));
                            break;
                        }
                        apply(parser.feed(decoder.decode(value, { stream: true })));
                    }
                    if (my !== seqRef.current) return;
                    // Stream closed without a terminal event (connection drop):
                    // whatever prose arrived stands; mark it done so the cursor stops.
                    setState((prev) =>
                        prev.phase === "streaming" ||
                        prev.phase === "facts" ||
                        prev.phase === "connecting"
                            ? {
                                  ...prev,
                                  phase: prev.text ? "done" : "unavailable",
                                  reason: prev.text ? prev.reason : "stream_ended",
                                  totalMs: Math.round(performance.now() - t0),
                              }
                            : prev,
                    );
                } catch (err) {
                    if (my !== seqRef.current) return;
                    if ((err as { name?: string })?.name === "AbortError") return;
                    setState((prev) => ({
                        ...prev,
                        phase: "error",
                        reason: "network",
                        totalMs: Math.round(performance.now() - t0),
                    }));
                }
            })();
        },
        [enabled, searchBaseUrl, markDead],
    );

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    return { state, start, cancel, probe, available: enabled && available };
}

// ── The driver: stopping points, not keystrokes ─────────────────────────────

/**
 * How long the live input must be still before an overview-shaped query is
 * treated as finished. The search itself debounces at 300 ms; the overview
 * waits longer because it is a request to a model, not a lookup, and a
 * half-typed question is not a question. Enter / chips / did-you-mean / a
 * URL skip the wait entirely (commit()).
 */
export const RAPID_SETTLE_MS = 1100;

export interface IUseRapidOverview extends IUseRapidAnswer {
    /**
     * The live input is overview-shaped (and the endpoint is available): the
     * slot reserves the card frame now, before any request is made.
     */
    armed: boolean;
    /**
     * Explicit stopping point — Enter with nothing highlighted, a "Try
     * asking" chip, a did-you-mean correction, the page's URL. Starts as
     * soon as the search for that exact query exists; a no-op for a lookup
     * and for a searchId already started.
     */
    commit: (query: string) => void;
}

interface ISearchForOverview {
    searchId: string;
    searchQuery: string;
    phase: string;
}

/**
 * Drive the overview from the committed search state AND the live input.
 *
 *   live input changes            -> settle timer restarts; a running
 *                                    overview for a different query aborts
 *   still for settleMs (armed)    -> pending = live; start once the search
 *                                    for it exists
 *   commit(query)                 -> pending = query; start likewise, now
 *   search phase returns to idle  -> everything cancels
 *
 * At most one start per searchId. The search's uuid is the correlation id,
 * so answer telemetry joins the search, the result list and the clicks.
 */
export function useRapidOverviewForSearch(
    search: ISearchForOverview,
    liveQuery: string,
    searchBaseUrl: string,
    getToken?: () => Promise<string>,
    settleMs: number = RAPID_SETTLE_MS,
): IUseRapidOverview {
    const rapid = useRapidAnswer(searchBaseUrl, getToken);
    const { start, cancel, available, probe } = rapid;
    const live = liveQuery.trim();
    // No base url (v1 builder, missing config) means no endpoint, and
    // `available` is false until the server's flag probe says yes: never
    // reserve a frame that nothing could ever fill.
    const overviewShaped = searchBaseUrl.length > 0 && wantsOverview(live);
    const armed = available && overviewShaped;

    // The first overview-shaped input is the moment to ask the server whether
    // the surface is on at all (cached per page load; no-op when it is not).
    useEffect(() => {
        if (overviewShaped) probe();
    }, [overviewShaped, probe]);

    // Latest search / answer state for the timer and commit paths, synced
    // after every render (before the effects below, which are declared later).
    const searchRef = useRef(search);
    const stateRef = useRef(rapid.state);
    useEffect(() => {
        searchRef.current = search;
        stateRef.current = rapid.state;
    });
    const pendingRef = useRef<string | null>(null);
    const startedForRef = useRef("");
    const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearSettle = useCallback(() => {
        if (settleRef.current) {
            clearTimeout(settleRef.current);
            settleRef.current = null;
        }
    }, []);

    const tryStart = useCallback(() => {
        const want = pendingRef.current;
        if (!want) return;
        const s = searchRef.current;
        if (!s.searchId || s.phase === "idle" || s.searchQuery.trim() !== want) return;
        pendingRef.current = null;
        if (startedForRef.current === s.searchId) return;
        if (!wantsOverview(want)) return;
        startedForRef.current = s.searchId;
        start(want, s.searchId);
    }, [start]);

    const commit = useCallback(
        (query: string) => {
            clearSettle();
            const q = query.trim();
            if (!q) return;
            if (wantsOverview(q)) probe();
            pendingRef.current = q;
            tryStart();
        },
        [clearSettle, tryStart, probe],
    );

    // Live input: a keystroke is never a stopping point. Restart the settle
    // timer, drop an overview the input has moved away from, keep a commit
    // that still matches what is typed.
    useEffect(() => {
        clearSettle();
        const running = stateRef.current;
        if (running.phase !== "idle" && running.query !== live) {
            startedForRef.current = "";
            cancel();
        }
        if (pendingRef.current !== null && pendingRef.current !== live) {
            pendingRef.current = null;
        }
        if (!armed) return undefined;
        settleRef.current = setTimeout(() => {
            settleRef.current = null;
            pendingRef.current = live;
            tryStart();
        }, settleMs);
        return clearSettle;
    }, [live, armed, settleMs, clearSettle, cancel, tryStart]);

    // Search state: the pending stopping point starts once its search exists;
    // idle (clear / close / cancel) takes everything down with it.
    useEffect(() => {
        if (search.phase === "idle") {
            clearSettle();
            pendingRef.current = null;
            if (startedForRef.current !== "") {
                startedForRef.current = "";
                cancel();
            }
            return;
        }
        tryStart();
    }, [search.searchId, search.searchQuery, search.phase, clearSettle, cancel, tryStart]);

    return { ...rapid, armed, commit };
}
